import { Button, FlagKeyPill, SelectField, TextField } from '@shared/ui';
import { useMemo, useState } from 'react';

import {
    ALL_PROVIDERS,
    deselectClient,
    matchingClients,
    selectAllMatching,
    selectClient,
    selectionChips,
    type ClientOption,
    type ProviderFilterView,
} from './client-selection';

/** How many matches are drawn at once; the selection itself has no cap. */
const VISIBLE_MATCHES = 50;

/**
 * Searchable client picker with the selection as removable chips (ACR-1596).
 * Controlled: the parent holds the ordered tenant ids.
 *
 * `providers` adds the merchant-provider filter. It is a read of its own, so
 * while it is loading or has failed the select is replaced by a notice and the
 * search keeps working.
 */
export function ClientSelector({
    clients,
    selected,
    onChange,
    providers,
    disabled = false,
}: {
    clients: readonly ClientOption[];
    selected: readonly string[];
    onChange: (next: readonly string[]) => void;
    providers?: ProviderFilterView;
    disabled?: boolean;
}) {
    const [search, setSearch] = useState('');
    const [chosenProvider, setChosenProvider] = useState(ALL_PROVIDERS);
    // A provider that left the data (a refetch) stops filtering instead of hiding everyone.
    const provider = providers?.options.includes(chosenProvider) ? chosenProvider : ALL_PROVIDERS;
    const byTenant = providers?.byTenant ?? null;
    const matches = useMemo(
        () => matchingClients(clients, { search, provider }, byTenant),
        [clients, search, provider, byTenant],
    );
    const chosen = useMemo(() => new Set(selected), [selected]);
    const chips = useMemo(() => selectionChips(selected, clients), [selected, clients]);
    const all = selectAllMatching(selected, matches);

    return (
        <div className="flex flex-col gap-3">
            <TextField
                label="Clients"
                value={search}
                onChange={setSearch}
                placeholder="Search by name, subdomain or id"
                disabled={disabled}
            />
            {providers?.available && providers.options.length > 0 && (
                <SelectField
                    label="Merchant provider"
                    value={provider}
                    onChange={setChosenProvider}
                    options={[
                        { value: ALL_PROVIDERS, label: 'All merchant providers' },
                        ...providers.options.map((option) => ({ value: option, label: option })),
                    ]}
                    disabled={disabled}
                />
            )}
            {providers?.notice !== undefined && (
                <p role="status" className="text-xs text-content-muted">
                    {providers.notice}
                </p>
            )}
            <ul aria-label="Matching clients" className="flex max-h-48 flex-col overflow-y-auto">
                {matches.slice(0, VISIBLE_MATCHES).map((client) => {
                    const isSelected = chosen.has(client.tenantId);
                    return (
                        <li key={client.tenantId}>
                            <button
                                type="button"
                                disabled={disabled}
                                aria-pressed={isSelected}
                                onClick={() => {
                                    onChange(
                                        isSelected
                                            ? deselectClient(selected, client.tenantId)
                                            : selectClient(selected, client.tenantId),
                                    );
                                }}
                                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-surface-sunken"
                            >
                                <span>{client.displayName}</span>
                                <span className="text-xs text-content-muted">
                                    {isSelected
                                        ? 'Selected'
                                        : (client.subdomain ?? client.tenantId)}
                                </span>
                            </button>
                        </li>
                    );
                })}
                {matches.length === 0 && (
                    <li className="px-2 py-1.5 text-xs text-content-muted">No clients match.</li>
                )}
            </ul>
            {matches.length > VISIBLE_MATCHES && (
                <p className="text-xs text-content-muted">
                    Showing {String(VISIBLE_MATCHES)} of {String(matches.length)}. Refine the
                    search.
                </p>
            )}
            {all.added > 0 && (
                <div>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => {
                            onChange(all.selected);
                        }}
                    >
                        Select all {String(all.added)} matching
                    </Button>
                </div>
            )}
            <div aria-label="Selected clients" className="flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                    <span
                        key={chip.tenantId}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-accent-line bg-accent-surface px-2.5 py-1 text-[12.5px] font-medium text-accent-text"
                    >
                        <FlagKeyPill>{chip.label}</FlagKeyPill>
                        <button
                            type="button"
                            disabled={disabled}
                            aria-label={`Remove ${chip.label}`}
                            onClick={() => {
                                onChange(deselectClient(selected, chip.tenantId));
                            }}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
}
